import React, { useState, useEffect, useMemo, useRef } from 'react';
import { format, addDays, parseISO, isAfter } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Building, User, Euro, Calendar, Clock, AlertTriangle } from 'lucide-react';
import type { CalendarAffectation, AffectationFormData } from '@/types/calendar';
import { ChantierGroupedSelect } from '@/components/calendar/ChantierGroupedSelect';
import WeekendConfirmDialog, { isWeekend } from '@/components/ui/WeekendConfirmDialog';
import { z } from "zod";
import { useToast } from "@/hooks/use-toast";
import { useModalProtection } from '@/hooks/useModalProtection';

// Schema de validation pour le formulaire d'affectation
const affectationFormSchema = z.object({
  chantier_id: z.string().min(1, "Veuillez sélectionner un chantier"),
  membre_equipe_id: z.string().min(1, "Veuillez sélectionner un membre"),
  date_debut: z.string().min(1, "La date de début est requise"),
  date_fin: z.string().min(1, "La date de fin est requise"),
  heures_par_jour: z.number().min(0.5, "Minimum 0.5 heure/jour").max(12, "Maximum 12 heures/jour"),
  jours_travailles: z.number().min(0.5, "Minimum 0.5 jour").max(365, "Maximum 365 jours"),
  notes: z.string().max(500, "500 caractères max"),
}).refine(
  data => data.date_fin >= data.date_debut,
  { message: "La date de fin doit être après la date de début", path: ["date_fin"] }
);

interface AffectationModalProps {
  isOpen: boolean;
  onClose: () => void;
  affectation: CalendarAffectation | null;
  defaultDate: Date;
  chantiers: any[];
  membres: any[];
  equipes: any[];
  onSave: (data: AffectationFormData) => Promise<boolean>;
  onUpdate: (id: string, data: Partial<AffectationFormData>) => Promise<boolean>;
  defaultMembreId?: string; // Pré-sélectionner un membre
}

// Calculer jours ouvrés (lundi-vendredi, weekends exclus par défaut)
// Avec option pour inclure le jour de fin s'il tombe un weekend et que l'utilisateur confirme
const calculateJoursOuvres = (debut: Date, fin: Date, inclureWeekendFin: boolean = false): number => {
  let count = 0;
  // Normaliser les dates à minuit pour éviter les problèmes d'heures
  const current = new Date(debut);
  current.setHours(0, 0, 0, 0);
  const finNormalized = new Date(fin);
  finNormalized.setHours(23, 59, 59, 999); // Fin de journée pour inclure le dernier jour

  while (current <= finNormalized) {
    const dayOfWeek = current.getDay();
    // Vérifier si c'est le dernier jour (même date)
    const isLastDay = current.toDateString() === fin.toDateString();
    // 0 = dimanche, 6 = samedi (weekends)
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      count++;
    } else if (isLastDay && inclureWeekendFin) {
      // Si c'est le dernier jour et qu'on inclut le weekend, le compter
      count++;
    }
    current.setDate(current.getDate() + 1);
  }
  return count;
};

const AffectationModal = ({
  isOpen,
  onClose,
  affectation,
  defaultDate,
  chantiers,
  membres,
  equipes,
  onSave,
  onUpdate,
  defaultMembreId,
}: AffectationModalProps) => {
  const isEdit = !!affectation?.id;
  const { toast } = useToast();
  useModalProtection(isOpen);

  const [formData, setFormData] = useState<AffectationFormData>({
    chantier_id: '',
    type_affectation: 'membre',
    membre_equipe_id: '',
    equipe_id: '',
    date_debut: format(defaultDate, 'yyyy-MM-dd'),
    date_fin: format(addDays(defaultDate, 6), 'yyyy-MM-dd'),
    heures_par_jour: 8,
    jours_travailles: 5,
    notes: '',
  });

  const [loading, setLoading] = useState(false);

  // État pour le popup de confirmation weekend
  const [showWeekendDialog, setShowWeekendDialog] = useState(false);
  const [pendingDateFin, setPendingDateFin] = useState<string>('');
  const [inclureWeekendFin, setInclureWeekendFin] = useState(false);

  // Initialiser le formulaire quand le modal s'ouvre
  // Fix B2 : wasOpenRef empêche le reset du formulaire quand les props changent
  // en arrière-plan (causé par Realtime reconnexion après tab switch)
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      // Modal vient de s'ouvrir → initialiser le formulaire
      if (affectation?.id) {
        // Mode édition
        setFormData({
          chantier_id: affectation.chantier_id,
          type_affectation: affectation.equipe_id ? 'equipe' : 'membre',
          membre_equipe_id: affectation.membre_equipe_id,
          equipe_id: affectation.equipe_id || '',
          date_debut: affectation.date_debut,
          date_fin: affectation.date_fin,
          heures_par_jour: affectation.heures_par_jour || 8,
          jours_travailles: affectation.jours_travailles,
          notes: affectation.notes || '',
        });
      } else {
        // Mode création - pré-sélectionner le membre si fourni
        // Date de fin par défaut = demain
        setFormData({
          chantier_id: '',
          type_affectation: 'membre',
          membre_equipe_id: defaultMembreId || '',
          equipe_id: '',
          date_debut: format(defaultDate, 'yyyy-MM-dd'),
          date_fin: format(addDays(defaultDate, 1), 'yyyy-MM-dd'),
          heures_par_jour: 8,
          jours_travailles: 1,
          notes: '',
        });
      }
      wasOpenRef.current = true;
    }
    if (!isOpen) {
      wasOpenRef.current = false;
    }
  }, [isOpen, affectation, defaultDate, defaultMembreId]);

  // Pré-remplir les dates avec celles du chantier sélectionné
  useEffect(() => {
    if (formData.chantier_id && chantiers.length > 0 && !isEdit) {
      const selectedChantier = chantiers.find((c) => c.id === formData.chantier_id);
      if (selectedChantier) {
        const dateDebut = selectedChantier.date_debut_prevue || selectedChantier.date_debut;
        const dateFin = selectedChantier.date_fin_prevue || selectedChantier.date_fin_estimee;
        if (dateDebut && dateFin) {
          setFormData((prev) => ({
            ...prev,
            date_debut: dateDebut,
            date_fin: dateFin,
          }));
        }
      }
    }
  }, [formData.chantier_id, chantiers, isEdit]);

  // Calcul automatique des jours travaillés (lundi-vendredi, weekends exclus sauf confirmation)
  useEffect(() => {
    if (formData.date_debut && formData.date_fin) {
      const debut = new Date(formData.date_debut);
      const fin = new Date(formData.date_fin);
      const joursOuvres = calculateJoursOuvres(debut, fin, inclureWeekendFin);
      setFormData((prev) => ({ ...prev, jours_travailles: Math.max(1, joursOuvres) }));
    }
  }, [formData.date_debut, formData.date_fin, inclureWeekendFin]);

  // Gérer le changement de date de fin avec vérification weekend
  const handleDateFinChange = (newDateFin: string) => {
    if (!newDateFin) {
      setFormData({ ...formData, date_fin: '' });
      return;
    }
    const date = parseISO(newDateFin);
    if (isWeekend(date)) {
      // La date tombe un weekend, demander confirmation
      setPendingDateFin(newDateFin);
      setShowWeekendDialog(true);
    } else {
      // Jour de semaine, appliquer directement
      setFormData({ ...formData, date_fin: newDateFin });
      setInclureWeekendFin(false);
    }
  };

  // Callback du dialogue weekend
  const handleWeekendConfirm = (inclure: boolean) => {
    setInclureWeekendFin(inclure);
    setFormData({ ...formData, date_fin: pendingDateFin });
    setShowWeekendDialog(false);
    setPendingDateFin('');
  };

  // Membre sélectionné pour le calcul du coût
  const selectedMembre = useMemo(() => {
    return membres.find((m) => m.id === formData.membre_equipe_id);
  }, [membres, formData.membre_equipe_id]);

  // Nom du chantier sélectionné (pour affichage dynamique)
  const selectedChantierNom = useMemo(() => {
    const chantier = chantiers.find((c) => c.id === formData.chantier_id);
    return chantier?.nom_chantier || null;
  }, [chantiers, formData.chantier_id]);

  // Preview du coût : taux_horaire * coefficient_charges * heures_par_jour
  const coutPreview = useMemo(() => {
    if (!selectedMembre) return null;

    const tauxHoraire = selectedMembre.taux_horaire || 0;
    const coef = selectedMembre.coefficient_charges || 1.85;

    const coutJournalier =
      formData.heures_par_jour * tauxHoraire * coef;
    const coutTotal = coutJournalier * formData.jours_travailles;

    return {
      tauxHoraire,
      coutJournalier,
      coutTotal,
      heures: formData.heures_par_jour,
      jours: formData.jours_travailles,
    };
  }, [selectedMembre, formData.heures_par_jour, formData.jours_travailles]);

  // Détection du dépassement de la date de fin du chantier
  const depassementDateFin = useMemo(() => {
    if (!formData.chantier_id || !formData.date_fin) return null;

    const selectedChantier = chantiers.find((c) => c.id === formData.chantier_id);
    if (!selectedChantier) return null;

    const dateFinChantier = selectedChantier.date_fin_prevue || selectedChantier.date_fin_estimee;
    if (!dateFinChantier) return null;

    const dateFinAffectation = parseISO(formData.date_fin);
    const dateFinChantierParsed = parseISO(dateFinChantier);

    if (isAfter(dateFinAffectation, dateFinChantierParsed)) {
      return {
        dateFinChantier,
        dateFinChantierFormatted: format(dateFinChantierParsed, 'dd/MM/yyyy'),
      };
    }
    return null;
  }, [formData.chantier_id, formData.date_fin, chantiers]);

  // État pour confirmation du dépassement
  const [depassementConfirme, setDepassementConfirme] = useState(false);

  // Reset confirmation quand la date change
  useEffect(() => {
    setDepassementConfirme(false);
  }, [formData.date_fin, formData.chantier_id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation Zod
    const validation = affectationFormSchema.safeParse(formData);
    if (!validation.success) {
      validation.error.issues.forEach(issue => {
        toast({ title: "Erreur", description: issue.message, variant: "destructive" });
      });
      return;
    }

    setLoading(true);

    try {
      let success: boolean;
      if (isEdit && affectation) {
        success = await onUpdate(affectation.id, formData);
      } else {
        success = await onSave(formData);
      }

      if (success) {
        onClose();
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Calendar className="h-5 w-5 text-primary" />
              {isEdit ? 'Modifier l\'affectation' : 'Nouvelle affectation'}
            </DialogTitle>
            <DialogDescription>
              {isEdit
                ? 'Modifiez les détails de l\'affectation'
                : 'Affectez un membre ou une équipe à un chantier'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            {/* Chantier */}
            <div className="space-y-2">
              <Label htmlFor="chantier" className="font-semibold flex items-center gap-2">
                <Building className="h-4 w-4" />
                Chantier *
              </Label>
              <ChantierGroupedSelect
                chantiers={chantiers}
                value={formData.chantier_id}
                onValueChange={(value) => setFormData({ ...formData, chantier_id: value })}
              />
            </div>

            {/* Membre */}
            <div className="space-y-2">
              <Label htmlFor="membre" className="font-semibold flex items-center gap-2">
                <User className="h-4 w-4" />
                Membre *
              </Label>
              <Select
                value={formData.membre_equipe_id}
                onValueChange={(value) => setFormData({ ...formData, membre_equipe_id: value })}
              >
                <SelectTrigger id="membre">
                  <SelectValue placeholder="Sélectionner un membre" />
                </SelectTrigger>
                <SelectContent>
                  {membres.map((membre) => (
                    <SelectItem key={membre.id} value={membre.id}>
                      {membre.prenom} {membre.nom}
                      {membre.poste && ` (${membre.poste})`}
                      {membre.taux_horaire && ` - ${membre.taux_horaire}€/h`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="date_debut" className="font-semibold">
                  Date de début *
                </Label>
                <Input
                  id="date_debut"
                  type="date"
                  value={formData.date_debut}
                  onChange={(e) => setFormData({ ...formData, date_debut: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="date_fin" className="font-semibold">
                  Date de fin *
                </Label>
                <Input
                  id="date_fin"
                  type="date"
                  value={formData.date_fin}
                  onChange={(e) => handleDateFinChange(e.target.value)}
                  required
                  className={depassementDateFin ? 'border-warning' : ''}
                />
              </div>
            </div>

            {/* Avertissement dépassement date de fin du chantier */}
            {depassementDateFin && (
              <div className="p-3 bg-warning/10 border border-warning/30 rounded-lg space-y-2">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-warning">
                      Date de fin au-delà du chantier
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      La date de fin d'affectation dépasse la date de fin prévue du chantier ({depassementDateFin.dateFinChantierFormatted}).
                    </p>
                  </div>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={depassementConfirme}
                    onChange={(e) => setDepassementConfirme(e.target.checked)}
                    className="w-4 h-4 rounded border-warning text-warning focus:ring-warning"
                  />
                  <span className="text-xs text-foreground">
                    Je confirme vouloir affecter au-delà de la date de fin du chantier
                  </span>
                </label>
              </div>
            )}

            {/* Heures et Jours */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="heures" className="font-semibold flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Heures/jour {selectedChantierNom && <span className="font-normal text-muted-foreground">sur {selectedChantierNom}</span>}
                </Label>
                <Input
                  id="heures"
                  type="number"
                  step="0.5"
                  min="0.5"
                  max="12"
                  value={formData.heures_par_jour}
                  onChange={(e) =>
                    setFormData({ ...formData, heures_par_jour: parseFloat(e.target.value) || 8 })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="jours" className="font-semibold">
                  Jours travaillés {selectedChantierNom && <span className="font-normal text-muted-foreground">sur {selectedChantierNom}</span>}
                </Label>
                <Input
                  id="jours"
                  type="number"
                  min="0.5"
                  step="0.5"
                  value={formData.jours_travailles}
                  onChange={(e) =>
                    setFormData({ ...formData, jours_travailles: parseFloat(e.target.value) || 0.5 })
                  }
                />
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes" className="font-semibold">
                Notes
              </Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Instructions particulières, précisions..."
                rows={2}
              />
            </div>

            {/* Preview du coût */}
            {coutPreview && (
              <Card className="bg-primary/5 border-primary/20">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Euro className="h-5 w-5 text-primary" />
                    <span className="font-semibold">Coût estimé</span>
                  </div>
                  <div className="space-y-1 text-sm">
                    <p className="text-muted-foreground">
                      {selectedMembre?.prenom} {selectedMembre?.nom} : {coutPreview.tauxHoraire}€/h ×{' '}
                      {coutPreview.heures}h = <strong>{coutPreview.coutJournalier.toFixed(0)}€/jour</strong>
                    </p>
                    <div className="border-t pt-2 mt-2">
                      <p className="text-lg font-bold text-primary">
                        Total ({coutPreview.jours} jours) : {coutPreview.coutTotal.toFixed(0)}€
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={loading || !formData.chantier_id || !formData.membre_equipe_id || (depassementDateFin && !depassementConfirme)}
            >
              {loading ? 'Enregistrement...' : isEdit ? 'Modifier' : 'Créer l\'affectation'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>

      {/* Dialogue de confirmation weekend */}
      <WeekendConfirmDialog
        isOpen={showWeekendDialog}
        onClose={() => {
          setShowWeekendDialog(false);
          setPendingDateFin('');
        }}
        onConfirm={handleWeekendConfirm}
        dateFin={pendingDateFin ? parseISO(pendingDateFin) : new Date()}
        type="membre"
      />
    </Dialog>
  );
};

export default AffectationModal;
